<template>
  <div class="community-home-wrapper bg-white">
    <Head>
      <Title>{{ $messages("community.title") }}</Title>
      <Meta name="description" :content="$messages('community.description')" />
      <Link
        v-for="(link, index) in hrefLang()"
        :key="index"
        :rel="link.rel"
        :href="link.href"
        :hid="link.hid"
        :hreflang="link.hreflang"
      />
    </Head>
    <CommunityHero
      :title="getHeroContent.title"
      :img="getHeroContent.imageUrl"
      :to="getTo(getHeroContent)"
      :breadcrumbs="breadcrumbs"
      :skip-animation="skipHeroAnimation"
      @diasporaChange="onDiasporaChange"
    />
    <div class="container">
      <CommunityFilter class="mt-4 lg:mt-9" />
    </div>
    <div class="container mt-6 md:mt-12">
      <CommunityItemList
        :items="items"
        :title="getTitle"
        :class="{
          'mb-12': !hasMore,
        }"
      />

      <div v-if="hasMore" class="text-center my-12 lg:mb-18">
        <button
          class="btn btn-infra-violette"
          :disabled="loading"
          @click="loadMore"
        >
          <div v-if="loading" class="spinner text-white" role="status">
            <span class="sr-only">Loading...</span>
          </div>
          {{ $messages("community.labels.load_more") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'pinia'
import {
  COUNTRY_COOKIE_KEY,
  SUPPORTED_CULTURES,
  COMMUNITY_TAGS
} from '@/utils/constants'
import { communityV2Store } from '~/store/communityV2'
import {
  searchAllByTag
} from '@/api/community'

export default {
  name: 'CommunityHome',
  layout: 'community',
  setup () {
    const { setLocale } = useI18n()

    definePageMeta({
      key: to => to.fullPath,
      middleware: ['community'],
      stopDefaultSwitch: true
    })

    watchEffect(() => {
      if (useRoute().params.slug === 'search') {
        refreshNuxtData()
      }
    })

    return {
      setLocale
    }
  },
  data () {
    return {
      step: 10,
      content: [],
      items: [],
      itemsCount: 0,
      loading: false,
      hasItemList: false,
      skipHeroAnimation: false,
      breadcrumbs: [
        {
          title: this.$messages('label.home'),
          to: this.$mRouter.getLocalRoute({ key: 'index' })
        },
        {
          title: this.$messages('page_labels.community')
        }
      ]
    }
  },
  computed: {
    ...mapState(communityV2Store, {
      getActiveContent: 'getActiveContent',
      getArticleCategories: 'getArticleCategories',
      getAllArticleCategories: 'getAllArticleCategories',
      homeContent: 'homeContent'
    }),
    getHeroContent () {
      return this.content ? this.content.find(x => this.isHeroBlock(x.type)) || {} : {}
    },
    getTitle () {
      return this.getActiveContent.slug.replace(/-/g, ' ')
    },
    hasMore () {
      return this.items ? (this.items.length < this.itemsCount) : false
    }
  },
  activated () {
    this.updateActiveContent()
    this.$track.pageView(this.$route, this.getTrackObject())
  },
  created () {
    this.updateActiveContent()
  },
  async mounted () {
    await this.load()
    this.$bus.onDynamicRouteChangeLanguage(
      this.$route.meta.key,
      this.handleLocaleSwitch
    )

    this.$track.pageView(this.$route, {
      name: `Community Tags`,
      component: `/community/tag/${this.$route.params.slug}/`
    })
  },
  methods: {
    ...mapActions(communityV2Store, {
      setActiveContent: 'setActiveContent',
      loadArticleCategories: 'loadArticleCategories'
    }),
    async load () {
      const locale = this.$i18n.locale
      const promises = []
      promises.push(searchAllByTag(locale, this.$route.params.slug, this.step, 0))
      this.hasItemList = true
      const response = await Promise.all(promises)
      this.content = this.homeContent
      this.items = this.hasItemList ? response[0].items : []
      this.itemsCount = this.hasItemList ? response[0].count : 0
    },
    isHeroBlock (type) {
      return type === 'native-community-hero-block-record'
    },
    getMargins (currentItemType) {
      if (!this.isArticleBlock(currentItemType)) {
        return 'my-6 md:my-24'
      }

      return 'my-6 md:my-12'
    },
    getTo (item) {
      if (item.contentType === 'video') {
        return this.$mRouter.getLocalRoute({
          name: 'community-majority-tv-slug',
          params: { slug: item.slug }
        })
      }

      if (item.contentType === 'event') {
        return this.$mRouter.getLocalRoute({
          name: 'community-event-slug',
          params: { slug: item.slug }
        })
      }

      if (item.contentType === 'discount') {
        return this.$mRouter.getLocalRoute({
          name: 'community-discount-slug',
          params: { slug: item.slug }
        })
      }

      return this.$mRouter.getLocalRoute({
        name: 'community-article-slug',
        params: { slug: item.slug }
      })
    },
    async loadMore () {
      try {
        this.loading = true
        const countryId = this.$cookies.get(COUNTRY_COOKIE_KEY)
        this.items = [
          ...this.items,
          ...(
            await searchAllByTag(
              this.$i18n.locale,
              this.$route.params.slug,
              this.step,
              this.items.length,
              countryId
            )
          ).items
        ]
      } finally {
        this.loading = false
      }
    },
    updateActiveContent () {
      this.setActiveContent({ type: COMMUNITY_TAGS })
    },
    onDiasporaChange () {
      window.location.reload()
    },
    getTrackObject () {
      let response = {}
      switch (this.$route.meta.key) {
        case 'Community':
          response = {
            name: 'CommunityHome',
            component: this.$options.name
          }
          break
        case 'CommunitySearchResult':
          response = {
            name: 'CommunitySearchResult',
            component: this.$options.name
          }
          break
        case 'CommunityCategory':
          response = {
            name: `CommunityCategory - ${this.getTitle}`,
            slug: this.$route.params.slug,
            categoryId: this.$route.params.slug,
            categoryName: this.getTitle,
            type: 'category',
            component: this.$options.name
          }
          break
        case 'CommunityArticles':
          response = {
            name: 'CommunityArticles',
            component: this.$options.name
          }
          break
        case 'CommunityEvents':
          response = {
            name: 'CommunityEvents',
            component: this.$options.name
          }
          break
        case 'CommunityVideos':
          response = {
            name: 'CommunityVideos',
            component: this.$options.name
          }
          break
      }
      return response
    },
    handleLocaleSwitch (val) {
      this.setLocale(val.destinationLocale).then(() => {
        const resolvedRoute = this.$mRouter.getLocalRoute({ name: 'community' }).href
        window.location.href = resolvedRoute
      })
    },
    hrefLang () {
      const alternatives = []
      const supportedLanguages = SUPPORTED_CULTURES.map(
        culture => culture.twoLetter
      )
      if (this.$route.meta.key === 'CommunityCategory') {
        let currentCategory = null
        const allCategories = this.getAllArticleCategories()
        if (allCategories) {
          Object.keys(allCategories).forEach((lang) => {
            allCategories[lang].forEach((category) => {
              if (category.slug === this.$route.params.slug) {
                currentCategory = category
              }
            })
          })
          if (currentCategory) {
            supportedLanguages.forEach((lang) => {
              const translatedCategory = this.getArticleCategories(lang).find(
                category => category.id === currentCategory.id
              )
              if (lang === 'en') {
                translatedCategory &&
                        alternatives.push({
                          hid: 'default-alternate',
                          rel: 'alternate',
                          hreflang: 'x-default',
                          href:
                            this.$env.websiteUrl +
                            this.$router
                              .resolveLocalRoute(
                                {
                                  key: this.$route.meta.key,
                                  params: { slug: this.$route.params.slug }
                                },
                                lang
                              )
                              .href.replace(
                                currentCategory.slug,
                                translatedCategory.slug
                              )
                        })
                translatedCategory &&
                        alternatives.push({
                          hid: `${lang}-alternate`,
                          rel: 'alternate',
                          hreflang: lang,
                          href:
                            this.$env.websiteUrl +
                            this.$router
                              .resolveLocalRoute(
                                {
                                  key: this.$route.meta.key,
                                  params: { slug: this.$route.params.slug }
                                },
                                lang
                              )
                              .href.replace(
                                currentCategory.slug,
                                translatedCategory.slug
                              )
                        })
              } else {
                translatedCategory &&
                        alternatives.push({
                          hid: `${lang}-alternate`,
                          rel: 'alternate',
                          hreflang: lang,
                          href:
                            this.$env.websiteUrl +
                            this.$router
                              .resolveLocalRoute(
                                {
                                  key: this.$route.meta.key,
                                  params: { slug: this.$route.params.slug }
                                },
                                lang
                              )
                              .href.replace(
                                currentCategory.slug,
                                translatedCategory.slug
                              )
                        })
              }
            })
          }
        }
      } else {
        supportedLanguages.forEach((lang) => {
          if (lang === 'en') {
            alternatives.push({
              hid: 'default-alternate',
              rel: 'alternate',
              hreflang: 'x-default',
              href:
                      this.$env.websiteUrl +
                      this.$mRouter.resolveLocalRoute(
                        {
                          key: this.$route.meta.key,
                          params: { slug: this.$route.params.slug }
                        },
                        lang
                      ).href
            })
            alternatives.push({
              hid: 'en-alternate',
              rel: 'alternate',
              hreflang: lang,
              href:
                      this.$env.websiteUrl +
                      this.$mRouter.resolveLocalRoute(
                        {
                          key: this.$route.meta.key,
                          params: { slug: this.$route.params.slug }
                        },
                        lang
                      ).href
            })
          } else {
            alternatives.push({
              hid: `${lang}-alternate`,
              rel: 'alternate',
              hreflang: lang,
              href:
                      this.$env.websiteUrl +
                      this.$mRouter.resolveLocalRoute(
                        {
                          key: this.$route.meta.key,
                          params: { slug: this.$route.params.slug }
                        },
                        lang
                      ).href
            })
          }
        })
      }
      return alternatives
    }
  }
}
</script>

      <style lang="postcss" scoped></style>
